import { ToastMessage } from '@utility/toast';
import { mappingErrorMessage } from '../lib/mapping-errors';
import { excludeBody } from '../lib/mappable-params';
import type {
  ConfirmDecision,
  ConfirmStatus,
  StatusAction,
} from '../lib/suggestion-types';
import { useConfirmSuggestions } from './use-confirm-suggestions';
import { useListingMapping } from './use-listing-mapping';
import { useSkipRestore } from './use-skip-restore';

const LINK_MESSAGES: Record<ConfirmStatus | 'not_sent', string> = {
  mapped: 'Listing linked to the product',
  unchanged: 'Listing was already linked to that product',
  changed_meanwhile:
    'This listing changed meanwhile. Check it before linking it again.',
  not_found: 'This listing no longer exists.',
  not_sent: 'The link was not saved. Try again.',
};

const STATUS_MESSAGES: Record<StatusAction, string> = {
  skip: 'moved to Skipped',
  restore: 'moved back to To review',
};

const isLinked = (status: ConfirmStatus | 'not_sent') =>
  status === 'mapped' || status === 'unchanged';

/**
 * The single-row writes of the Mapping tab, each with its toast. Each returns
 * whether it worked, so a dialog can stay open on a failure.
 */
export function useRowActions() {
  const confirm = useConfirmSuggestions();
  const statusChange = useSkipRestore();
  const mapping = useListingMapping();

  const link = async (decision: ConfirmDecision) => {
    const { outcomes, error } = await confirm.mutateAsync({
      decisions: [decision],
    });
    const status = outcomes[0]?.status ?? 'not_sent';

    if (error) {
      ToastMessage.error(mappingErrorMessage(error));
    } else if (isLinked(status)) {
      ToastMessage.success(LINK_MESSAGES[status]);
    } else {
      ToastMessage.error(LINK_MESSAGES[status]);
    }

    return isLinked(status);
  };

  const move = async (action: StatusAction, listingIds: string[]) => {
    try {
      await statusChange.mutateAsync({ action, listingIds });
      ToastMessage.success(
        `${listingIds.length === 1 ? 'Listing' : `${listingIds.length} listings`} ${STATUS_MESSAGES[action]}`,
      );
      return true;
    } catch (failure) {
      ToastMessage.error(mappingErrorMessage(failure));
      return false;
    }
  };

  const exclude = async (listingId: string) => {
    try {
      await mapping.mutateAsync({ listingId, body: excludeBody });
      ToastMessage.success('Listing excluded successfully');
      return true;
    } catch (failure) {
      ToastMessage.error(mappingErrorMessage(failure));
      return false;
    }
  };

  return {
    link,
    move,
    exclude,
    isBusy: confirm.isPending || statusChange.isPending || mapping.isPending,
  };
}
